import { I18n } from "@assets/strings";
import React, { useEffect, useMemo } from "react";
import { useHistory } from "@core/routing/useHistory";
import { Redirect } from "@core/routing/redirect";
import { useLocation } from "react-router";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { config } from "@core/config/config";
import { Environment } from "@core/config/environment";
import { Path, ProfileTab, profileTabs } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { ExperienceType } from "@domain/experience/experience";
import { useUserProfile } from "@domain/user/hooks";
import { useIsMobile, useScrollToHash } from "@utils/hooks";
import { AppAuthIncentive } from "@ui/components/appAuthIncentive";
import { ApplicationStoryCard } from "@ui/components/application/applicationStoryCard";
import { ScrollBehavior } from "@ui/components/navigation/scrollToTop";
import { Page } from "@ui/components/page";
import { FeedList } from "@ui/components/post/feedList";
import { StickyColumn } from "@ui/components/stickyColumn";
import { TabSelector } from "@ui/components/tabSelector";
import { useModalTools } from "@ui/modals/modalContext";
import { gap, mobile } from "@ui/styles/utils";
import { CongralutationModal } from "@ui/pages/application/applicationEditorPage/congralutation";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { ContentList } from "@ui/pages/profilePage/content/contentList";
import { FavoriteList } from "@ui/pages/profilePage/favorites/favoriteList";
import { GroupList } from "./groupList";
import { MyOrganizations } from "./myOrganizations";
import { ProfileCard } from "./profileCard";
import { ProfileExperiences, ProfileExperiencesSkeleton } from "./profileExperiences";
interface ProfilePageProps {
	profileId?: string;
	profileSlug?: string;
	tab?: ProfileTab;
	action: "trackEmail";
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ profileId, profileSlug, tab = "activity", action }) => {
	if (!profileId && !profileSlug) {
		return <Redirect to={Path.Root} />;
	}

	const { result: profile, loading, error } = useUserProfile(profileId ?? "", profileSlug);
	const currentProfile = useCurrentProfile();
	const isMe = !!currentProfile && profile?.id === currentProfile.id;
	const history = useHistory();
	const location = useLocation();
	const { createModal } = useModalTools();
	const { trackingService } = useServices();
	//const contentCountList = isMe ? currentProfile?.contentCountList : profile?.contentCountList;
	useEffect(() => {
		if (profile?.slug && profileSlug !== profile?.slug) {
			history.push(Path.Profile(profile));
		}

		if (location.search && location.state !== undefined) {
			createModal((close) => (
				<CongralutationModal
					applicationId={location.state.applicationId}
					canonicalLink={`${Environment.APP_BASE_URL}${location.pathname}`}
					close={close}
				/>
			));
		}
	}, [profile?.slug]);

	useEffect(() => {
		if (action === "trackEmail") {
			trackingService.track("trackEmail - Followups");
		}
	}, []);
	// const displayedTabs = profileTabs;

	const displayedTabs: readonly ProfileTab[] = useMemo(() => {
		if (isMe) {
			return profileTabs;
		} else {
			return profileTabs.filter((tab) => tab !== "favorite");
		}
	}, [isMe]);

	const isMobile = useIsMobile();
	useScrollToHash(100);

	if (error?.code === 404) {
		return <NotFoundPage message={I18n.t("404.user")} />;
	}

	if (displayedTabs.indexOf(tab) < 0) {
		return <Redirect to={Path.Profile({ id: profileId, slug: profileSlug })} />;
	}

	return !profile ? (
		loading ? (
			<>
				<ClipLoader />
			</>
		) : null
	) : (
		<Container>
			{/* <Seo
				type="website"
				title={I18n.t("seo.profile.title", { name: fullName(profile) })}
				description={[profile.description].filter(Boolean).join(" - ")}
				ogImage={!profile.avatar ? defaultProfilePicture : profile.avatar?.url}
				canonicalLink={`${Config.APP_BASE_URL}${location.pathname}`}
			/> */}
			<ProfileCardCell profile={profile} />
			{isMobile ? (
				currentProfile ? (
					<MobileOrganizationsColumn>
						{/* {!!currentProfile.applicationId && (
							<ApplicationStoryCard applicationId={currentProfile?.applicationId} profile={profile} />
						)} */}
						<ApplicationStoryCard profile={profile} />
						{isMe && <MyOrganizations />}
						<ProfileExperiences type={ExperienceType.ACADEMIC} profileId={profile.id} />
						{config.features.professionalExperiences ? (
							<ProfileExperiences type={ExperienceType.PROFESSIONAL} profileId={profile.id} />
						) : null}
					</MobileOrganizationsColumn>
				) : (
					<ApplicationStoryCard profile={profile} />
				)
			) : (
				<OrganizationsColumn topOffset={120} bottomOffset={30}>
					<ApplicationStoryCard profile={profile} />
					{isMe && <MyOrganizations />}
					{currentProfile ? (
						<>
							<ProfileExperiences type={ExperienceType.ACADEMIC} profileId={profile.id} />
							{config.features.professionalExperiences ? (
								<ProfileExperiences type={ExperienceType.PROFESSIONAL} profileId={profile.id} />
							) : null}
						</>
					) : (
						<>
							<ProfileExperiencesSkeleton type={ExperienceType.ACADEMIC} />
							{config.features.professionalExperiences ? (
								<ProfileExperiencesSkeleton type={ExperienceType.PROFESSIONAL} />
							) : null}
						</>
					)}
				</OrganizationsColumn>
			)}
			{currentProfile ? (
				<FeedContainer>
					<TabSelector
						id="profile-tab"
						items={displayedTabs}
						selectedIndex={displayedTabs.indexOf(tab)}
						onItemSelected={(index) =>
							history.push(
								Path.Profile(profile, index === 0 ? undefined : displayedTabs[index]),
								ScrollBehavior.NoScroll
							)
						}
						itemRenderer={(item) =>
							item === "activity"
								? isMe
									? I18n.t("profile.tab.post_self")
									: I18n.t("profile.tab.post")
								: item === "content"
									? isMe
										? I18n.t("profile.tab.content_self")
										: I18n.t("profile.tab.content")
									: item === "shareroom"
										? isMe
											? I18n.t("profile.tab.groups")
											: I18n.t("search.filter.type.SHAREROOM")
										: I18n.t("profile.tab.favorite")
						}
					/>
					{tab === "activity" ? (
						<FeedList user={profile} />
					) : tab === "favorite" ? (
						<FavoriteList userId={profile.id} />
					) : tab === "shareroom" ? (
						isMe ? (
							<GroupList isMe={isMe} />
						) : (
							<GroupList isMe={isMe} profile={profile} />
						)
					) : (
						<ContentList user={profile} />
					)}
				</FeedContainer>
			) : (
				<AppAuthIncentive message={I18n.t("profile.auth.incentive", { name: profile.primaryName })} />
			)}
		</Container>
	);
};

const Container = styled(Page)`
	display: grid;
	column-gap: 45px;
	row-gap: 45px;
	justify-content: center;
	grid-template-columns: minmax(0, 1fr) 350px;
	grid-template-rows: auto;
	grid-template-areas:
		"profile-card organizations"
		"feed organizations"
		"feed ."
		"feed .";

	@media ${mobile} {
		row-gap: 30px;
		grid-template-columns: 100%;
		grid-template-rows: auto;
		grid-template-areas:
			"profile-card"
			"organizations"
			"feed";
	}
`;

const ProfileCardCell = styled(ProfileCard)`
	grid-area: profile-card;
`;

const FeedContainer = styled.div`
	grid-area: feed;
	${gap(40)}
`;

const OrganizationsColumn = styled(StickyColumn)`
	grid-area: organizations;
	${gap(48)};
`;

const MobileOrganizationsColumn = styled.div`
	grid-area: organizations;
	${gap(20)};
`;
